import { Box, Flex, Text } from '@mantine/core'
import { useState, useEffect, useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import { InputCurrency, TextCurrency } from '@flecto/ui'

import { useTaxesInfo } from '../../../taxes'
import { useFormContext } from './FormBookingProduct'

type FormSectionPriceDepositProps = {
  resetCustomValue: boolean
}

export function FormSectionPriceDeposit({
  resetCustomValue,
}: FormSectionPriceDepositProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })
  const form = useFormContext()

  const [depositPerItem, setDepositPerItem] = useState(0)
  const [depositSubtotal, setDepositSubtotal] = useState(0)
  const [pricePerItem, setPricePerItem] = useState(0)
  const [priceSubtotal, setPriceSubtotal] = useState(0)

  const {
    getTaxBracket,
    getCalculatedAmounts,
    userDefaultTaxBracket,
    userDefaultTaxable,
  } = useTaxesInfo()

  const taxBracket = getTaxBracket(form.values.tax_rate ?? '')

  const handleChangeDepositPerItem = (cents: number) => {
    setDepositPerItem(cents)
    calculateDepositSubtotal(cents * (form.values.quantity ?? 1))
  }

  const calculateDepositPerItem = (cents: number) => {
    setDepositPerItem(cents / (form.values.quantity ?? 1))
  }

  const calculateDepositSubtotal = (cents: number) => {
    setDepositSubtotal(cents)
    form.setFieldValue('deposit_cents', cents)
  }

  const rentalTotalTaxes = useMemo(() => {
    const amounts = getCalculatedAmounts(
      priceSubtotal,
      form.values.tax_rate ?? '',
      form.values.taxable ?? true,
      true
    )

    return amounts.taxes.cents
  }, [form.values, priceSubtotal])

  const rentalTotalExcludingTaxes = useMemo(() => {
    const amounts = getCalculatedAmounts(
      priceSubtotal,
      form.values.tax_rate ?? userDefaultTaxBracket.value,
      form.values.taxable ?? userDefaultTaxable,
      true
    )

    return amounts.totalWithoutTaxes.cents
  }, [form.values, priceSubtotal])

  const rentalTotalIncludingTaxes = useMemo(() => {
    const amounts = getCalculatedAmounts(
      priceSubtotal,
      form.values.tax_rate ?? '',
      form.values.taxable ?? true,
      true
    )

    return amounts.totalWithTaxes.cents
  }, [form.values, priceSubtotal])

  const calculatePricePerItem = (cents: number, isInputing = false) => {
    const amountsPerItem = getCalculatedAmounts(
      cents,
      form.values.tax_rate ?? userDefaultTaxBracket.value,
      form.values.taxable ?? userDefaultTaxable,
      isInputing
    )
    setPricePerItem(amountsPerItem.input.cents)
  }

  const calculatePriceSubtotal = (cents: number, isInputing = false) => {
    const amountsSubtotal = getCalculatedAmounts(
      cents,
      form.values.tax_rate ?? userDefaultTaxBracket.value,
      form.values.taxable ?? userDefaultTaxable,
      isInputing
    )

    setPriceSubtotal(amountsSubtotal.input.cents)
  }

  useEffect(() => {
    const amount = depositPerItem * (form.values.quantity ?? 1)

    setDepositSubtotal(amount)
    form.setFieldValue('deposit_cents', amount)
  }, [form.values.quantity, depositPerItem])

  useEffect(() => {
    calculatePricePerItem(form.values.pricing_tier?.price_cents ?? 0)
    calculatePriceSubtotal(form.values.pricing_tier?.price_cents ?? 0)
    calculateDepositPerItem(form.values.deposit_cents ?? 0)
    calculateDepositSubtotal(form.values.deposit_cents ?? 0)
  }, [form.values.pricing_tier?.price_cents, form.values.tax_rate])

  useEffect(() => {
    const amounts = getCalculatedAmounts(
      pricePerItem * (form.values.quantity ?? 1),
      form.values.tax_rate ?? '',
      form.values.taxable ?? true,
      true
    )

    setPriceSubtotal(amounts.input.cents)
  }, [form.values.quantity, pricePerItem])

  useEffect(() => {
    handleChangeDepositPerItem(form.values.deposit_cents ?? 0)
  }, [resetCustomValue])

  return (
    <Box mt='lg'>
      <Flex my='md' wrap='wrap' direction='column'>
        <Text fw={500}>{t('label_deposit')}</Text>
        <Text color='dimmed' size='sm'>
          {t('label_deposit_placeholder')}
        </Text>
        <InputCurrency
          style={{ flex: 1 }}
          value={depositPerItem}
          onChange={handleChangeDepositPerItem}
        />
      </Flex>

      <Flex justify='space-between' align='center'>
        <Text>{t('label_price_rental_excluding_taxes')}</Text>
        <TextCurrency cents={rentalTotalExcludingTaxes} />
      </Flex>

      <Flex justify='space-between' align='center'>
        <Text color={form.values.taxable ? undefined : 'dimmed'}>
          {t('label_taxes', {
            replace: { count: form.values.taxable ? taxBracket.percentage : 0 },
          })}
        </Text>
        <TextCurrency size='md' cents={rentalTotalTaxes} />
      </Flex>

      <Flex justify='space-between' align='center'>
        <Text>{t('label_price_rental_including_taxes')}</Text>
        <TextCurrency cents={rentalTotalIncludingTaxes} />
      </Flex>

      <Flex justify='space-between' align='center'>
        <Text>{t('label_price_deposit_total')}</Text>
        <TextCurrency cents={depositSubtotal} />
      </Flex>
    </Box>
  )
}
